import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/user';
import { NumberField, TextField, useValidation } from '../../components/form';

import { Errors, Successes } from '../../components/common/msg';
import { post } from '../../libs/api';
import {
  showDateTime,
  showStrikePrice,
  showPrice,
  showPercent,
  roundDownAmount,
  roundDownToDecimalPlaces,
  truncateToDecimalPlaces,
  truncateAmount,
  showAmount,
  showDate,
  getPrecisionOnCurrency,
  toSettlementCcy,
} from '../../libs/util';
import {
  isDecimalWithinScale,
  truncateCurrencyAmount,
} from '../../libs/currencyPrecision';
import DropDown from '../../components/form/dropdown';
import CalculatePlan from '../../components/invest/calculate';
import useIndicesStore from '../../../src/stores/useIndicesStore';

import Modal from 'react-bootstrap/Modal';
import useInterval from '../../libs/useInterval';
import CurrencySelect from '../../components/form/currencySelect';
import ReferenceNumber from '../../components/form/referrenceNumber';

// The client may fund a DCI from any of these wallets; the reference rate converts
// whatever they pay in into the product's notional currency.
const INVEST_CURRENCY_OPTIONS = [
  { value: 'USDC', label: 'USDC' },
  { value: 'USDT', label: 'USDT' },
  { value: 'BTC', label: 'BTC' },
  { value: 'ETH', label: 'ETH' },
  { value: 'USD', label: 'USD' },
  { value: 'SGD', label: 'SGD' },
  { value: 'HKD', label: 'HKD' },
];

function Page() {
  const REFRESH_INTERVAL = 5; //parseInt(process.env.REACT_APP_COUNTDOWN);
  const CONFIRM_INTERVAL = 30;
  const CANCEL_TIMEOUT = 120000; // 2 minutes
  const DCI_MIN_AMOUNT = 5000; // Min invest amount

  const { indices } = useIndicesStore();
  const [isTradeMtn, setIsTradeMtn] = useState(false);
  const { planType, sourceCurrency } = useParams();
  const isSellHigh =
    planType.toUpperCase() === 'USD' ||
    planType.toUpperCase() === 'USDC' ||
    planType.toUpperCase() === 'USDT';
  const [balance, setBalance] = useState(0);
  const [title, setTitle] = useState('');
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState('');
  // const [sourceCurrency, setSourceCurrency] = useState(null);
  const [expiredAmount, setExpiredAmount] = useState(0);
  const [exercisedAmount, setExercisedAmount] = useState(0);

  const [maturity, setMaturity] = useState(null);
  const [maturityOptions, setMaturityOptions] = useState([]);

  const [strike, setStrike] = useState(null);
  const [strikeOptions, setStrikeOptions] = useState([]);

  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [productStartDate, setProductStartDate] = useState(null);

  const [errors, setErrors] = useState([]);
  const [errors2, setErrors2] = useState([]);
  const [successes, setSuccesses] = useState([]);

  const [showError, setShowError] = useState(false);
  const [setFieldValid, isValid] = useValidation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [timer, setTimer] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [countdownInterval, setCountdownInterval] = useState(REFRESH_INTERVAL);
  const [cancelTimeout, setCancelTimeout] = useState(null);

  const [btn, setBtn] = useState('Review');

  const [accept, setAccept] = useState(false);
  const [showTc, setShowTc] = useState(false);
  const [isTermsRead, setIsTermsRead] = useState(false);

  const navigate = useNavigate();

  const [selectedCurrency, setSelectedCurrency] = useState();
  // Funding wallets the client actually holds, restricted to the DCI-eligible
  // currencies. Drives the dropdown so an unheld currency is never selectable.
  const [investWallets, setInvestWallets] = useState([]);
  // Notional is fixed by the product, never chosen: Sell High settles in the crypto
  // being sold (BTC/ETH), Buy Low settles in USDC.
  const notionalCcy = isSellHigh ? sourceCurrency.toUpperCase() : 'USDC';
  // What the client receives if the conversion event happens.
  const conversionCcy = isSellHigh ? 'USDC' : planType.toUpperCase();
  const [desiredFixedRate, setDesiredFixedRate] = useState('');
  const [thresholdError, setThresholdError] = useState('');
  const [referenceRate, setReferenceRate] = useState('1.000000');
  const [noationalAmount, setNoationalAmount] = useState();
  const notionalEditRef = useRef(false);
  const [isReferenceEdited, setIsReferenceEdited] = useState(false);

  // PB-2143. The server is authoritative on whether a requested rate is above the
  // live offer rate. When /investment/order/add responds with requiresRateAck
  // instead of booking, nothing has been recorded — the client must show the
  // board rate the server just quoted and re-post the SAME order plus
  // rate_ack_board_rate before anything is created. `rateAck` holds the info to
  // render while that confirmation is pending; `pendingOrderRef` holds the base
  // order to resubmit (never mutated with a stale ack value, so a second
  // requiresRateAck response always re-sends the NEW board rate, not the old one).
  const [rateAck, setRateAck] = useState(null);
  const pendingOrderRef = useRef(null);

  // PB-1871. The rate field is seeded once per strike the user selects, keyed on
  // strike.id (the product id) rather than on the identity of the `strike` object.
  // The price refresh timer rebuilds that object every tick, so keying on identity
  // re-ran the seed continuously and could overwrite a rate the user had typed —
  // which is how a stale 52.71% reached the server in place of an intended 40%.
  const seededForStrikeId = useRef(null);

  // The board rate that was on screen when the field was seeded, and the movement
  // since. Recorded so the user is told when the market moves under them instead of
  // the number silently changing (or silently not changing) beneath a typed rate.
  const [quotedBoardRate, setQuotedBoardRate] = useState(null);
  const [boardRateMoved, setBoardRateMoved] = useState(null);

  // Movement worth interrupting for, in APR decimal fraction. 0.005 = half a point.
  const BOARD_MOVE_NOTICE = 0.005;

  useEffect(() => {
    if (!products || !maturity || !strike) return;

    const liveBoardRate = parseFloat(strike.product.calc_adj_apy);
    if (isNaN(liveBoardRate)) return;

    if (seededForStrikeId.current !== strike.id) {
      // A different strike: seed the field and remember what we quoted.
      seededForStrikeId.current = strike.id;
      setDesiredFixedRate((liveBoardRate * 100).toFixed(2));
      setQuotedBoardRate(liveBoardRate);
      setBoardRateMoved(null);
      return;
    }

    // Same strike, refreshed price. Never touch the field — only report movement.
    if (
      quotedBoardRate != null &&
      Math.abs(liveBoardRate - quotedBoardRate) >= BOARD_MOVE_NOTICE
    ) {
      setBoardRateMoved({ from: quotedBoardRate, to: liveBoardRate });
    }
  }, [strike]);

  useEffect(() => {
    if (notionalEditRef.current) {
      notionalEditRef.current = false;
      return;
    }
    if (amount && referenceRate) {
      const calculatedNotional = parseFloat(amount) * parseFloat(referenceRate);
      if (!isNaN(calculatedNotional)) {
        setNoationalAmount(truncateAmount(calculatedNotional, notionalCcy));
      }
    }
  }, [amount, referenceRate]);

  useEffect(() => {
    if (selectedCurrency) {
      onSelectedCurrencyChange();
    }
  }, [selectedCurrency]);

  const onSelectedCurrencyChange = async () => {
    // load wallet
    setLoading(true);
    const wallets = await fetchWallets();
    if (!wallets) {
      setErrors(['Could not get wallet balances']);
      setLoading(false);
      return;
    }
    setInvestWallets(
      wallets.filter((w) =>
        INVEST_CURRENCY_OPTIONS.some(
          (o) => o.value === w.currency.toUpperCase()
        )
      )
    );
    const matched = wallets.find(
      (w) => w.currency.toUpperCase() === selectedCurrency.toUpperCase()
    );
    setWallet(matched || null);
    setLoading(false);
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.target.value);
  };

  const fetchWallets = async () => {
    const data = await post('/wallet/balance', {});
    if (data?.status === 200) {
      return data.data;
    }
    return null;
  };

  const checkTradeMtnStatus = async () => {
    const data = await post('/auth/mtn/trade');
    if (data?.data?.trademtn) {
      // it is live
      setIsTradeMtn(false);
      setErrors2([]);
    } else {
      setIsTradeMtn(true);
      setErrors2([
        'Investment application is unavailable. Please try again later.',
      ]);
    }
  };

  const fetchProducts = async (currency, sourceCurrency) => {
    const currencyAsSource =
      sourceCurrency.toUpperCase() === 'USDC' ||
      sourceCurrency.toUpperCase() === 'USDT'
        ? 'usd'
        : sourceCurrency;

    const currencyToFetchProduct =
      currency.toUpperCase() === 'USDC' || currency.toUpperCase() === 'USDT'
        ? 'usd'
        : currency;

    return await post('/investment/product/list', {
      currency: currencyToFetchProduct,
      sourceCurrency: currencyAsSource,
      productType: 'hydi',
    });
  };

  useInterval(() => {
    if (!planType || !sourceCurrency) {
      return;
    }
    if (countdown == 0) {
      checkTradeMtnStatus();
      reloadProduct();
      setCountdown(countdownInterval);
      return;
    }
    setCountdown(countdown - 1);
  }, 1000);

  const loadProducts = async () => {
    setProducts([]);
    setDesiredFixedRate(null);
    // PB-1871: clearing the rate must clear what it was seeded for, or the next
    // strike with the same id would not re-seed.
    seededForStrikeId.current = null;
    setQuotedBoardRate(null);
    setBoardRateMoved(null);
    setMaturity(null);
    setStrike(null);
    setProduct(null);

    // load
    const data = await fetchProducts(planType, sourceCurrency);
    if (data?.status != 200) {
      console.log('error', data);
      setErrors([data.error]);
      return;
    }

    if (data.data?.length == 0) {
      setErrors(['No matching product. Please choose another plan.']);
      return;
    }

    setProducts(data.data);
  };

  const reloadProduct = async () => {
    const data = await fetchProducts(planType, sourceCurrency);
    if (data?.status == 200) {
      setProducts(data.data);
      setProductStrikeOptions();
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const maxDecimals = getPrecisionOnCurrency(
      selectedCurrency || sourceCurrency
    );
    if (isDecimalWithinScale(value, maxDecimals)) {
      setAmount(value);
      setNoationalAmount(truncateAmount(value * referenceRate, notionalCcy));
    }
  };

  const handleReference = (value) => {
    setReferenceRate(value);
  };

  const noationalChange = (value) => {
    const maxDecimals = getPrecisionOnCurrency(notionalCcy);
    if (isDecimalWithinScale(value, maxDecimals)) {
      notionalEditRef.current = true;
      setNoationalAmount(value);
      if (amount && parseFloat(amount) > 0) {
        setReferenceRate(parseFloat(value) / parseFloat(amount));
      }
    }
  };

  const fetchWallet = async () => {
    const data = await post('/wallet/balance', {});
    let currency = sourceCurrency.toUpperCase();
    if (currency == 'USD') {
      currency = 'USDC';
    }

    if (data?.status == 200) {
      for (const w of data.data) {
        if (w.currency == currency) {
          setSelectedCurrency(w.currency);
          return w;
        }
      }
    }
    return null;
  };

  const convertCurrency = (currency) => {
    currency = currency.toLowerCase();
    if (currency == 'usd') {
      currency = 'usdc';
    }
    return currency;
  };

  useEffect(() => {
    if (step == 0) {
      setCountdownInterval(REFRESH_INTERVAL);
      setCountdown(REFRESH_INTERVAL);
    } else {
      setCountdownInterval(CONFIRM_INTERVAL);
      setCountdown(CONFIRM_INTERVAL);
      // Start the 2-minute timeout
      if (cancelTimeout) {
        clearTimeout(cancelTimeout);
      }
      const timeout = setTimeout(() => {
        setStep(0);
      }, CANCEL_TIMEOUT);

      setCancelTimeout(timeout);

      return () => {
        if (cancelTimeout) {
          clearTimeout(cancelTimeout);
        }
      };
    }
  }, [step]);

  useEffect(() => {
    // update maturity list
    let data = {};
    let maxProduct = null;
    let found = product ? false : true;

    for (const p of products) {
      data[p.tenor] = showDateTime(p.option.maturity_datetime);
      if (!maxProduct || p.calc_adj_apy > maxProduct.calc_adj_apy) {
        maxProduct = p;
      }
      if (product && p.product_id == product.product_id) {
        setProduct(product);
        found = true;
      }
    }
    if (!found) {
      setErrors(['The product is no longer valid.']);
      setProduct(null);
    }

    const keys = Object.keys(data);

    let list = [];
    let maxMaturity = null;
    let newMaturity = null;
    for (let k of keys) {
      const item = {
        name: data[k],
        subtitle: '' + k + ' days left',
        tenor: k,
        id: k,
      };
      if (k == maxProduct?.tenor) {
        maxMaturity = item;
      }
      list.push(item);
      if (maturity && maturity.id == item.id) {
        // update maturity
        newMaturity = item;
      }
    }
    if (!newMaturity && list?.length > 0) {
      newMaturity = list[0];
    }

    setMaturityOptions(list);
    // setMaturity(newMaturity);
    // if (!maturity) {
    //     setMaturity(maxMaturity);
    // }
  }, [products]);

  const setProductStrikeOptions = async () => {
    let list = [];
    let newStrike = null;

    for (let p of products) {
      if (p.tenor == maturity?.tenor) {
        const item = {
          name:
            '' +
            p.option.quote_currency +
            '/USDC ' +
            showStrikePrice(p.option.strike_price, ''),
          subtitle: 'APR ' + showPercent(p.calc_adj_apy, 2),
          product: p,
          id: p.product_id,
        };
        list.push(item);

        if (strike && strike.id == item.id) {
          newStrike = item;
        }
      }
    }
    list.sort((p1, p2) => {
      return p1.product.option.strike_price > p2.product.option.strike_price
        ? -1
        : 1;
    });

    setStrikeOptions(list);
    setStrike(newStrike);
  };

  useEffect(() => {
    setDesiredFixedRate(null);
    // PB-1871: same reason as in loadProducts — keep the seed marker in step with
    // the value it seeded, so the two can never disagree.
    seededForStrikeId.current = null;
    setQuotedBoardRate(null);
    setBoardRateMoved(null);
    setProductStrikeOptions();
  }, [maturity]);

  useEffect(() => {
    if (product) {
      setErrors([]);
    }
  }, [product]);

  useEffect(() => {
    setProduct(strike ? strike.product : null);
  }, [strike]);

  const onCurrencyChange = async () => {
    // load wallet
    setLoading(true);
    const w = await fetchWallet();
    if (!w) {
      setErrors(['Could not get wallet balances']);
      setLoading(false);
      return;
    }

    setWallet(w);

    // load products
    await loadProducts();
    // if(timer){
    //   clearInterval(timer);
    // }
    // setTimer(setInterval(reloadProduct, 10000));
    setLoading(false);
  };

  useEffect(() => {
    if (sourceCurrency) {
      onCurrencyChange();
    }
  }, [sourceCurrency]);

  useEffect(() => {
    if (!planType || !sourceCurrency) {
      setTitle('');
      return;
    }
    let strTitle = '';
    // PB-1046: display USD as USDC so the header matches the plans card
    let dst = toSettlementCcy(planType);
    let src = toSettlementCcy(sourceCurrency);
    let subfix = ' - BUY LOW';
    if (dst == 'USDC' || dst == 'USDT') {
      strTitle = src + '/' + dst;
      subfix = ' - SELL HIGH';
    }
    if (src === 'USDC' || src === 'USDT') {
      strTitle = dst + '/' + src;
    }
    // if (src == 'USD' || src == 'USDC') {
    //   strTitle = dst + '/USD';
    // }
    setTitle(strTitle + subfix);
  }, [planType, sourceCurrency]);

  useEffect(() => {
    setBalance(wallet ? wallet.main_balance : 0);
  }, [wallet]);

  useEffect(() => {
    if (!product) {
      return;
    }

    // Yield accrues on the notional, not on what the client paid in — the two are
    // different currencies whenever the reference rate is not 1.
    let value = parseFloat(noationalAmount);
    if (isNaN(value)) {
      setExpiredAmount(0);
      setExercisedAmount(0);
      return;
    }

    let examt = 0.0;
    let interest = 0.0;

    let interest_decimal = 8;
    let conversion_decimal = 8;

    if (
      notionalCcy === 'USD' ||
      notionalCcy === 'USDC' ||
      notionalCcy === 'USDT'
    ) {
      interest_decimal = 2;
      conversion_decimal = 8;
    }

    let apyRate = parseFloat(product.calc_adj_apy);
    if (desiredFixedRate) apyRate = parseFloat(desiredFixedRate) / 100;

    interest = truncateToDecimalPlaces(
      (apyRate / 365) * parseFloat(product.tenor) * value,
      interest_decimal
    );
    // interest = parseFloat(desiredFixedRate ? (parseFloat(desiredFixedRate) / 365 * parseFloat(product.tenor) * value) :
    //     (parseFloat(product.calc_adj_apy) / 365 * parseFloat(product.tenor) * value).toFixed(interest_decimal));

    let valueInterest = truncateToDecimalPlaces(
      value + interest,
      interest_decimal
    );
    setExpiredAmount(valueInterest);

    if (product.option.option_type == 'PUT') {
      console.log('product.option.strike_price', product.option.strike_price);
      examt = parseFloat(
        valueInterest / parseFloat(product.option.strike_price)
      );
      console.log('examt', examt);
      setExercisedAmount(examt);
    } else {
      console.log('coming here for else');
      examt = parseFloat(
        valueInterest * parseFloat(product.option.strike_price)
      );
    }
    examt = truncateToDecimalPlaces(examt, conversion_decimal);
    console.log('examt', examt);
    setExercisedAmount(examt);
  }, [product, noationalAmount, desiredFixedRate]);

  const handleStep1Submit = async (e) => {
    e.preventDefault();

    const currentDate = new Date();
    const displayStartDateUTC = currentDate.toLocaleString('en-US', {
      timeZone: 'UTC',
    });
    setProductStartDate(displayStartDateUTC);

    setErrors([]);
    setThresholdError('');

    const desiredRate = parseFloat(desiredFixedRate);
    // PB-2143: no more tolerance band here — this is advisory only, against the
    // last-refreshed board rate in the browser. The server re-checks against its
    // own live rate at submit time and is authoritative; see requiresRateAck
    // handling in handleSubmit.
    const thresholdRate = parseFloat(product.calc_adj_apy) * 100;

    if (desiredRate > thresholdRate) {
      setThresholdError(
        'This rate is above the current offer rate. You will be asked to confirm before the order is placed.'
      );
    }
    if (!isValid('amount')) {
      setShowError(true);
      return;
    }

    const value = parseFloat(amount);
    if (isNaN(value)) {
      setErrors(['Please provide numeric value.']);
      return;
    }

    if (
      String(amount).lastIndexOf('.') >= 0 &&
      String(amount).length - String(amount).lastIndexOf('.') > 9
    ) {
      setErrors(['Please limit to 8 decimal places.']);
      return;
    }
    if (value > wallet?.main_balance) {
      setErrors(['Exceeded wallet balance.']);
      return;
    }
    if (!referenceRate || Number.isNaN(parseFloat(referenceRate))) {
      setErrors(['Reference rate is required.']);
      return;
    }
    if (!noationalAmount || Number.isNaN(parseFloat(noationalAmount))) {
      setErrors(['Notional amount is required.']);
      return;
    }

    // Size limits apply to the notional. Gate on that rather than on the funding
    // amount, whose currency may be a fiat one with no Deribit index (SGD, HKD).
    const notionalValue = parseFloat(noationalAmount);
    var currencyIndex = 1.0;
    if (
      notionalCcy !== 'USD' &&
      notionalCcy !== 'USDT' &&
      notionalCcy !== 'USDC'
    ) {
      currencyIndex = indices.find((index) =>
        index.name.toUpperCase().includes(notionalCcy)
      )?.price;
    }

    if (currencyIndex == null) {
      setErrors([
        `Unable to price ${notionalCcy} right now. Please try again shortly.`,
      ]);
      return;
    }
    const nominalAmount = notionalValue * currencyIndex;
    const minAmount = Math.max(DCI_MIN_AMOUNT, product.min_amount);

    if (minAmount > 0 && nominalAmount < minAmount) {
      setErrors([
        'Notional amount must be at least ' + showPrice(minAmount, 'USD'),
      ]);
      return;
    }

    if (product.max_amount > 0 && notionalValue > product.max_amount) {
      setErrors([
        'Notional amount must not exceed ' +
          showPrice(product.max_amount, notionalCcy),
      ]);
      return;
    }

    setStep(1);
    setIsTermsRead(false);
    // setShowTc(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (!isValid()) {
      setShowError(true);
      return;
    }

    if (submitting) {
      return;
    }

    if (!wallet) {
      setErrors([
        'Select a currency you hold a wallet in before placing this order.',
      ]);
      return;
    }

    try {
      setSubmitting(true);

      // submit an order
      // PB-1306: USD->USDC settlement coercion is now performed by the
      // backend (CurrencyDecimalUtils.normalizeForSettlement in
      // PmsTransactionCrypto.populateTxn / readOrderProduct). The frontend
      // sends the raw wallet currency so PubInvestmentController's wallet
      // validation (wallet.currency vs orderProduct.investAmountCcy) keeps
      // matching for USD-funded DCI orders.
      // Both settlement legs follow the notional, never the funding wallet.
      // Sell High holds BTC/ETH and converts into USDC; Buy Low holds USDC and
      // converts into the crypto.
      const termExercisedAmountCcy = isSellHigh
        ? 'USDC'
        : planType.toUpperCase();

      // Unconverted payout is the notional plus its yield, in the notional currency.
      const tgtCCy = notionalCcy;
      let order = {
        wallet_id: wallet.wallet_id,
        product_id: product.product_id,
        maturity_datetime: product.option.maturity_datetime,
        invest_amount: parseFloat(amount),
        invest_ccy: selectedCurrency,
        term_expired_amount: roundDownAmount(expiredAmount, tgtCCy),
        term_exercised_amount: roundDownAmount(
          exercisedAmount,
          termExercisedAmountCcy
        ),
        notional_amount_ccy: notionalCcy,
        term_expired_amount_ccy: tgtCCy,
        term_exercised_amount_ccy: termExercisedAmountCcy,
        term_apy: parseFloat(desiredFixedRate) / 100,
        // PB-2143: threshold_rate removed. The server derives its own threshold
        // from the live board rate and no longer reads the client's copy — this
        // also retires a bug where parseFloat was applied AFTER
        // `product.calc_adj_apy + THRESHOLD_MARGIN`, so a string calc_adj_apy
        // silently concatenated instead of adding and the margin was lost.
        reference_rate: parseFloat(referenceRate),
        notional_amount: parseFloat(noationalAmount),
      };
      // Kept so a requiresRateAck response (now, or a second time if the board
      // moves again) can resubmit the exact same order, plus rate_ack_board_rate.
      pendingOrderRef.current = order;
      const result = await post('/investment/order/add', order);

      if (result?.data?.requiresRateAck) {
        setRateAck({
          boardRate: result.data.boardRate,
          requestedRate: result.data.requestedRate,
        });
        return;
      }

      if (result?.status != 200) {
        console.log('list error', result);
        setErrors([result?.error]);
        return;
      }

      setSuccesses([
        'Your order to invest in this product has been submitted.',
      ]);
      setStep(2);
      navigate('/portal/investment/new_order/' + result.data.transaction_id);
    } catch (e) {
      console.log('catch error', e);
    } finally {
      setSubmitting(false);
    }
  };

  // PB-2143: resubmit the exact order the user last tried, plus the board rate
  // the server just quoted in rateAck. If the board has moved again the server
  // will send back another requiresRateAck with a fresh boardRate — handled the
  // same way, so repeated warnings always show the latest number, never a stale
  // one carried over from an earlier response.
  const handleRateAckConfirm = async (e) => {
    if (e) {
      e.preventDefault();
    }
    const baseOrder = pendingOrderRef.current;
    if (!baseOrder || rateAck?.boardRate == null) {
      return;
    }

    if (submitting) {
      return;
    }

    const orderWithAck = {
      ...baseOrder,
      rate_ack_board_rate: rateAck.boardRate,
    };

    try {
      setSubmitting(true);
      const result = await post('/investment/order/add', orderWithAck);

      if (result?.data?.requiresRateAck) {
        setRateAck({
          boardRate: result.data.boardRate,
          requestedRate: result.data.requestedRate,
        });
        return;
      }

      if (result?.status != 200) {
        console.log('list error', result);
        setErrors([result?.error]);
        setRateAck(null);
        return;
      }

      setSuccesses([
        'Your order to invest in this product has been submitted.',
      ]);
      setRateAck(null);
      setStep(2);
      navigate('/portal/investment/new_order/' + result.data.transaction_id);
    } catch (e) {
      console.log('catch error', e);
    } finally {
      setSubmitting(false);
    }
  };

  // Dismissing posts nothing and leaves the form exactly as it was — the user
  // can edit the rate or amount and try again.
  const handleRateAckDismiss = (e) => {
    if (e) {
      e.preventDefault();
    }
    setRateAck(null);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setStep(0);
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate('/portal/plans');
  };

  useEffect(() => {
    setBtn(step == 1 ? 'Agree to Invest' : 'Invest Now');
  }, [step]);

  const handleTcClose = (e) => {
    if (e) {
      e.preventDefault();
    }
    setAccept(false);
    setShowTc(false);
    setCountdown(CONFIRM_INTERVAL);
  };

  const handleTcAccept = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (isTermsRead) {
      setAccept(true);
      setShowTc(false);
      setCountdown(CONFIRM_INTERVAL);
    }
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop > e.target.clientHeight / 2;
    if (bottom) {
      setIsTermsRead(true);
    }
  };

  const formatDisplayTitle = (title) => {
    let src = sourceCurrency.toUpperCase();
    if (src === 'USDC' || src === 'USDT') {
      if (!title) return '';
      return title.replace(/USD-[CP]/, src);
    } else {
      if (!title) return '';
      return title.replace(/USD-[CP]/, 'USDC');
    }
  };

  return (
    <Layout>
      <div className="page-invest wide-xs m-auto" id="iv-step-container">
        <div className="nk-pps-apps">
          <div className="nk-pps-title text-center">
            <h3 className="title">{title}</h3>
            {/* <p className="caption-text"></p> */}
            <h1 className="icon-stack">
              <em
                className={
                  'bg-dark-dim icon ni ni-sign-' +
                  planType +
                  '-alt icon-stack-back'
                }
                title="Sell BTC for USDC at strike price."
              ></em>
              <em
                className={
                  'bg-' +
                  convertCurrency(sourceCurrency) +
                  '-dim icon ni ni-sign-' +
                  convertCurrency(sourceCurrency) +
                  '-alt icon-stack-front'
                }
                title="Sell BTC for USDC at strike price."
              ></em>
            </h1>
          </div>

          {successes?.length > 0 && <Successes msgs={successes} />}

          {errors2?.length > 0 && <Errors errors={errors2} />}

          {isTradeMtn == false && products?.length > 0 && (
            <>
              {step == 0 && (
                <div className="form-validate is-alter">
                  <div className="nk-pps-field form-group">
                    <div className="form-label-group">
                      <label className="form-label">Maturity Date</label>
                    </div>
                    <DropDown
                      dtype={'expiry_date'}
                      placeholder="Please choose an Maturity Date"
                      options={maturityOptions}
                      selectedOption={maturity}
                      onChange={(x) => setMaturity(x)}
                    />
                  </div>

                  <div className="nk-pps-field form-group">
                    <div className="form-label-group">
                      <label className="form-label">Strike Rate</label>
                    </div>
                    <DropDown
                      dtype={'strike_rate'}
                      placeholder="Please choose a Strike Rate"
                      options={strikeOptions}
                      selectedOption={strike}
                      onChange={(x) => setStrike(x)}
                    />
                  </div>

                  <div className="nk-pps-field form-group">
                    <div className="form-label-group">
                      <label className="form-label iv-invest-lb-fixed">
                        Desired Fixed Rate
                      </label>
                    </div>
                    <div className="form-control-group">
                      {!product && (
                        <>
                          <TextField
                            className="form-control form-control-lg form-control-number bg-white"
                            placeholder={'-'}
                            disabled={true}
                            value={null}
                            showError={showError}
                          />
                        </>
                      )}
                      {product && (
                        <>
                          <NumberField
                            className="form-control form-control-lg form-control-number bg-white"
                            placeholder={''}
                            value={desiredFixedRate}
                            required={true}
                            showError={showError}
                            onValidate={(valid) =>
                              setFieldValid('desiredFixedRate', valid)
                            }
                            onChange={(e) => {
                              setDesiredFixedRate(e.target.value);
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {errors?.length > 0 && <Errors errors={errors} />}

                  <div className="nk-pps-field form-group">
                    <div className="form-label-group">
                      <label className="form-label iv-invest-lb-fixed">
                        Investment Amount
                      </label>
                    </div>
                    <div className="form-control-group">
                      <div className="form-info">
                        <CurrencySelect
                          value={selectedCurrency}
                          onChange={handleCurrencyChange}
                          customCurrencyOptions={investWallets.map((w) => ({
                            value: w.currency,
                            label: w.currency,
                          }))}
                        />
                      </div>

                      {!product && (
                        <>
                          <TextField
                            className="form-control form-control-lg form-control-number bg-white"
                            placeholder={'-'}
                            disabled={true}
                            value={null}
                            required={true}
                            showError={showError}
                          />
                        </>
                      )}

                      {product && (
                        <>
                          <NumberField
                            className="form-control form-control-lg form-control-number bg-white"
                            placeholder={''}
                            value={amount}
                            required={true}
                            showError={showError}
                            onValidate={(valid) =>
                              setFieldValid('amount', valid)
                            }
                            onChange={handleAmountChange}
                          />
                        </>
                      )}
                    </div>
                    <div className="form-note-group">
                      {product && (
                        <>
                          <span className="form-note-alt">
                            <a
                              href="#"
                              onClick={(e) =>
                                setAmount(
                                  truncateCurrencyAmount(
                                    wallet?.main_balance,
                                    wallet?.currency
                                  )
                                )
                              }
                            >
                              Wallet Balance:{' '}
                              {showPrice(
                                wallet?.main_balance,
                                wallet?.currency,
                                null,
                                true
                              )}
                            </a>
                            &nbsp;
                            <button
                              className="btn btn-xs btn-primary"
                              onClick={(e) =>
                                setAmount(
                                  truncateCurrencyAmount(
                                    wallet?.main_balance,
                                    wallet?.currency
                                  )
                                )
                              }
                            >
                              MAX
                            </button>
                          </span>
                        </>
                      )}
                    </div>

                    <div className="nk-pps-field form-group">
                      <div className="form-label-group">
                        <label className="form-label iv-invest-lb-fixed">
                          Reference rate
                        </label>
                      </div>
                      <div className="form-control-group w-50">
                        {!product && (
                          <>
                            <TextField
                              className="form-control form-control-sm form-control-number bg-white"
                              placeholder={'-'}
                              disabled={true}
                              value={null}
                              required={true}
                              showError={showError}
                            />
                          </>
                        )}

                        {product && (
                          <>
                            <ReferenceNumber
                              className="form-control form-control-sm form-control-number bg-white"
                              placeholder={''}
                              min={0}
                              value={referenceRate}
                              required={true}
                              showError={true}
                              decimalPlaces={8}
                              onValidate={(valid) =>
                                setFieldValid('referenceRate', valid)
                              }
                              onChange={(e) => {
                                setIsReferenceEdited(true);
                                handleReference(e.target.value);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="nk-pps-field form-group">
                      <div className="form-label-group">
                        <label className="form-label iv-invest-lb-fixed">
                          Notional Amount
                        </label>
                      </div>
                      <div className="form-control-group">
                        <div className="form-info">{notionalCcy}</div>

                        {!product && (
                          <>
                            <TextField
                              className="form-control form-control-lg form-control-number bg-white"
                              placeholder={'-'}
                              disabled={true}
                              value={null}
                              required={true}
                              showError={showError}
                            />
                          </>
                        )}
                        {product && (
                          <>
                            <NumberField
                              className="form-control form-control-lg form-control-number bg-white"
                              placeholder={''}
                              value={noationalAmount}
                              required={true}
                              showError={showError}
                              decimalPlaces={getPrecisionOnCurrency(
                                notionalCcy
                              )}
                              onValidate={(valid) =>
                                setFieldValid('notionalAmount', valid)
                              }
                              onChange={(e) => {
                                noationalChange(e.target.value);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step == 0 && (
                <>
                  {!product && (
                    <>
                      <div className="nk-ivp-card card card-bordered card-full mt-4">
                        <div className="card-inner-group">
                          <CalculatePlan />
                        </div>
                      </div>
                    </>
                  )}

                  {product && (
                    <>
                      <div className="nk-ivp-card card card-bordered card-full mt-4">
                        <div className="card-inner-group">
                          <CalculatePlan
                            product={product}
                            sourceCurrency={notionalCcy}
                            targetCurrency={conversionCcy}
                            expiredAmount={expiredAmount}
                            exercisedAmount={exercisedAmount}
                            productAPR={desiredFixedRate}
                          />
                        </div>
                      </div>
                      <div className="nk-pps-field form-action mt-1">
                        <div className="nk-pps-action">
                          <a
                            href="#"
                            className="btn btn-lg btn-block btn-primary"
                            onClick={handleStep1Submit}
                          >
                            <span>Next</span>
                          </a>
                        </div>
                      </div>
                      {/* <div className="form-note text-base text-center mb-n1">By continue this, you agree to our investment terms and conditions.</div> */}
                    </>
                  )}
                </>
              )}

              {step == 1 && (
                <>
                  <div className="nk-ivp-card card card-bordered card-full mt-4">
                    <div className="card-inner-group">
                      <div className="pl-2 pr-2 pt-2">
                        <div className="card-title text-center">
                          <span className="title form-label">
                            {countdown} seconds to refresh
                          </span>
                          {/*<span className="title form-label text-danger font-weight-bold" style={{fontSize: '1.5rem'}}>{countdown} seconds to rate refresh</span>*/}
                        </div>
                        <div className="nk-ivp-summary card-text">
                          <div className="row">
                            <div className="col-6">
                              <span className="sub-text">Selected Product</span>
                              <span className="lead-text">
                                <span className=" text-dark">
                                  {formatDisplayTitle(product?.display_title)}
                                </span>
                              </span>
                            </div>
                            <div className="col-6">
                              <span className="sub-text">Invested Amount</span>
                              <span className="lead-text">
                                <span className="text-dark">
                                  {showAmount(
                                    parseFloat(amount),
                                    null,
                                    8,
                                    false
                                  )}{' '}
                                  {selectedCurrency?.toUpperCase()}
                                </span>
                              </span>
                            </div>
                          </div>

                          {/*<div className="row mt-1 pb-1 border-bottom">*/}
                          {/*  <div className="col-4 text-left pl-4">*/}
                          {/*    <span>Term Duration</span>                            */}
                          {/*  </div>*/}
                          {/*  <div className="col-8 text-right pr-4">                            */}
                          {/*    <span>{product.tenor} Days</span>*/}
                          {/*  </div>*/}
                          {/*</div>*/}
                          <div className="row mt-1 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Investment Date</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {showDateTime(productStartDate.toString())}
                              </span>
                            </div>
                          </div>

                          <div className="row mt-2 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Strike Rate</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {showStrikePrice(
                                  product.option?.strike_price,
                                  'USD'
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="row mt-2 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Desired Fixed Rate</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {desiredFixedRate
                                  ? desiredFixedRate + '%'
                                  : showPercent(product.calc_adj_apy, 2)}
                              </span>
                            </div>
                          </div>
                          <div className="row mt-2 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Current Rate</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {showPercent(product.calc_adj_apy, 2)}
                              </span>
                            </div>
                          </div>

                          <div className="row mt-1 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Maturity Date</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {showDateTime(
                                  product.option?.maturity_datetime
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="row mt-2 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Reference Rate</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>{referenceRate}</span>
                            </div>
                          </div>

                          <div className="row mt-2 pb-1 border-bottom">
                            <div className="col-4 text-left pl-4">
                              <span>Notional Amount</span>
                            </div>
                            <div className="col-8 text-right pr-4">
                              <span>
                                {showAmount(
                                  parseFloat(noationalAmount),
                                  null,
                                  8,
                                  false
                                )}{' '}
                                {notionalCcy}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <CalculatePlan
                        product={product}
                        sourceCurrency={notionalCcy}
                        targetCurrency={conversionCcy}
                        expiredAmount={expiredAmount}
                        exercisedAmount={exercisedAmount}
                        productAPR={desiredFixedRate}
                      />
                    </div>
                  </div>

                  {/* <div className="form-group mt-2 mb-1"> */}
                  {/* <div className="custom-control custom-control-xs custom-checkbox"> */}
                  {/* <CheckField className="custom-control-input"
                                                            requiredMsg=" You must accept our terms to proceed."
                                                            required={true} showError={showError}
                                                            onValidate={(valid) => setFieldValid('accept', valid)}
                                                            checked={accept}
                                                            onChange={(checked) => setAccept(checked)}
                                                >
                                                    I have read, understood and agree to the Product Fact Sheet.
                                                </CheckField> */}

                  {/* </div> */}
                  {/* </div> */}
                  {boardRateMoved && (
                    <div className="alert alert-warning">
                      The indicative rate for this strike has moved from{' '}
                      {(boardRateMoved.from * 100).toFixed(2)}% to{' '}
                      {(boardRateMoved.to * 100).toFixed(2)}% since you opened
                      it. Your entered rate has been left as-is — review it
                      before submitting.
                    </div>
                  )}
                  {thresholdError && (
                    <div className="alert alert-danger">{thresholdError}</div>
                  )}
                  {/* PB-2319: a refusal at confirmation (a block or rule) is shown here; the form's error box is not on this step. */}
                  {errors?.length > 0 && <Errors errors={errors} />}
                  <div className="nk-pps-field form-action">
                    <div className="nk-pps-action">
                      <a
                        href="#"
                        className="btn btn-lg btn-block btn-primary"
                        onClick={handleSubmit}
                      >
                        <span>Agree to Invest ({countdown})</span>
                        {submitting && (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        )}
                      </a>
                    </div>
                    <div className="nk-pps-action mt-1">
                      <a
                        href="#"
                        className="btn btn-lg btn-block btn-white"
                        onClick={handleCancel}
                      >
                        <span>Cancel</span>
                      </a>
                    </div>
                  </div>
                  <Modal show={!!rateAck} onHide={handleRateAckDismiss}>
                    <div className="modal-header">
                      <div className="modal-title h4">
                        Confirm Rate Above Current Offer
                      </div>
                      <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={handleRateAckDismiss}
                      >
                        X
                      </button>
                    </div>
                    <Modal.Body>
                      <p>
                        The current live offer rate is{' '}
                        <strong>
                          {rateAck ? (rateAck.boardRate * 100).toFixed(2) : '-'}
                          %
                        </strong>
                        . You are giving the client{' '}
                        <strong>
                          {rateAck
                            ? (rateAck.requestedRate * 100).toFixed(2)
                            : '-'}
                          %
                        </strong>
                        , which is above the current offer rate.
                      </p>
                      <p>
                        If you proceed, this will be recorded as a rate above
                        the live offer and the order will be placed at the
                        requested rate.
                      </p>
                      <div className="text-center">
                        <a
                          href="#"
                          className={`btn btn-block btn-primary${
                            submitting ? ' disabled' : ''
                          }`}
                          onClick={handleRateAckConfirm}
                        >
                          <span>Confirm and Place Order</span>
                          {submitting && (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          )}
                        </a>
                        <a
                          href="#"
                          className="btn btn-block btn-white mt-1"
                          onClick={handleRateAckDismiss}
                        >
                          <span>Cancel</span>
                        </a>
                      </div>
                    </Modal.Body>
                  </Modal>
                  <Modal show={showTc} onHide={handleTcClose}>
                    <div className="modal-header">
                      <div className="modal-title h4">
                        Penguin Secure Product Factsheet
                      </div>
                      <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={handleTcClose}
                      >
                        X
                      </button>
                    </div>
                    <Modal.Body>
                      <div className="Content" onScroll={handleScroll}>
                        <p>
                          <h5>BRIEF INFORMATION ON THE PRODUCT</h5>

                          <p>
                            <strong>What is this product about?</strong>
                          </p>
                          <p>
                            Penguin Secure is a simple fixed yield investment
                            product over a defined tenor. Principal and Yield
                            are typically paid out on expiry
                          </p>
                        </p>
                        <hr />
                        <p>
                          <h5>PRODUCT SUITABILITY</h5>
                          <p>
                            <strong> Who is this product suitable for?</strong>
                          </p>
                          <p>
                            This product offers *principal protection and
                            guarantees a fixed return on expiry. This is a
                            low-risk product that is market neutral. The
                            investment time horizon here is envisioned to be
                            short term
                          </p>
                        </p>
                        <hr />

                        <p>
                          <h5>KEY PRODUCT FEATURES</h5>

                          <p>
                            <strong>What am I investing in?</strong>
                          </p>
                          <p>
                            This structured product is designed to primarily
                            capitalize on the contango of futures contracts. By
                            employing basis trading strategies, the product aims
                            to generate returns through the difference between
                            spot prices and futures prices, and/or spreads
                            between dated and perpetual futures contracts
                            written on a particular Digital Payment Token (DPT).
                            The choice of Underlying is discretionary to Penguin
                            Securities Trading Pte. Ltd. (PST) but strictly
                            limited to supported assets by the off-exchange
                            settlement solution (OES) and/or select Liquidity
                            Providers (LPs), which may vary from time to time
                          </p>
                          <table className="table">
                            <tbody>
                              <tr>
                                <td>Principal Asset: </td>
                                <td>USDT / USDC</td>
                              </tr>
                              <tr>
                                <td>Minimum Investment: </td>
                                <td>*USDT 100,000 or equivalent</td>
                              </tr>
                              <tr>
                                <td>Underlying: </td>
                                <td>
                                  DPT spot and futures (dated and perpetual)
                                  contracts supported by OES and/or LPs
                                </td>
                              </tr>

                              <tr>
                                <td>Investment Tenure</td>
                                <td>3 – 12 months</td>
                              </tr>
                              <tr>
                                <td>*Principal Protection</td>
                                <td>Yes</td>
                              </tr>
                              <tr>
                                <td>Leverage:</td>
                                <td>No</td>
                              </tr>
                              <tr>
                                <td>Yield/Annual Percentage Rate (APR): </td>
                                <td>Fixed</td>
                              </tr>
                              <tr>
                                <td>Payment Frequency:</td>
                                <td>Upon expiry</td>
                              </tr>
                              <tr>
                                <td>Issue Date/Time:</td>
                                <td>
                                  Subscription date; also refers to the start of
                                  Yield accrual calculation
                                </td>
                              </tr>
                              <tr>
                                <td>Cut-Off time:</td>
                                <td>
                                  1600 (UTC+8); Any subscription at and after
                                  this time will start Yield accrual calculation
                                  the day after
                                </td>
                              </tr>
                              <tr>
                                <td>Maturity Date/Time:</td>
                                <td>
                                  {' '}
                                  Subscription terminates; also refers to the
                                  end of interest accrual calculation. It also
                                  triggers the settlement process whereby the
                                  Principal and Yield are returned
                                </td>
                              </tr>

                              <tr>
                                <td>Yield Calculation:</td>
                                <td>
                                  Principal Amount x APR x Investment Tenor Day
                                  count(actual)/Year Day count (365)
                                </td>
                              </tr>

                              <tr>
                                <td>Payout Asset:</td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Stable coins (DPT) only:</td>
                                <td>USDT/USDC</td>
                              </tr>
                              <tr>
                                <td>Settlement Process:</td>
                                <td>
                                  <table class="  table-borderless">
                                    <tr class="c3">
                                      <td class="c12" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">Principal</span>
                                        </p>
                                      </td>
                                      <td class="c28" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">DPT</span>
                                        </p>
                                      </td>
                                      <td class="c23" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">
                                            USD (if applicable)
                                          </span>
                                        </p>
                                      </td>
                                    </tr>
                                    <tr class="c3">
                                      <td class="c12" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">Payment Speed</span>
                                        </p>
                                      </td>
                                      <td class="c28" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">Real Time</span>
                                        </p>
                                      </td>
                                      <td class="c23" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">
                                            2 business days{' '}
                                          </span>
                                        </p>
                                      </td>
                                    </tr>
                                    <tr class="c3">
                                      <td class="c12" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">Limitations</span>
                                        </p>
                                      </td>
                                      <td class="c28" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">
                                            Blockchain Congestion
                                          </span>
                                        </p>
                                      </td>
                                      <td class="c23" colspan="1" rowspan="1">
                                        <p class="c10">
                                          <span class="c2">
                                            Banking hours and currency cut offs
                                          </span>
                                        </p>
                                      </td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </p>

                        <hr />

                        <p>
                          <h5>Issuer</h5>
                          <p>
                            <strong> Who am I investing with?</strong>
                          </p>
                          <p>Penguin Securities Trading Pte. Ltd.</p>
                        </p>

                        <hr />
                        <p>
                          <h5>Associated Restrictions</h5>
                          <p>
                            <strong>
                              What are the associated restrictions of this
                              product that I should know?
                            </strong>
                          </p>
                          <ul>
                            <li>
                              This is a non-transferable investment product
                            </li>
                            <li>
                              Investors should hold the product until maturity.
                              Any early redemption (if applicable) requests will
                              be at the discretion of PST, and will likely incur
                              penalties, and/or loss of Yield, which may incur
                              claw backs (if applicable)
                            </li>
                            <li>
                              Subscription in any other asset other than USDT/C
                              will be subject to discretionary conversion set by
                              PST. Such conversions are provided on an ancillary
                              basis and PST does not undertake any guarantee of
                              performance nor availability of the said facility
                            </li>
                            <li>
                              Rollover Yields are solely determined at
                              prevailing market conditions, taken at the start
                              of Rollover subscription
                            </li>
                            <li>
                              Principal and Yield are calculated on DPT basis
                              solely
                            </li>
                            <li>
                              Subscription and Expiry subject to business days
                              only
                            </li>
                          </ul>
                        </p>

                        <hr />
                        <p>
                          <h5>Fees</h5>
                          <p>
                            <strong>
                              What are the fees and charges I must pay?
                            </strong>
                          </p>
                          <p>
                            All fees and charges are imputed within the
                            investment’s structure and return mechanics
                          </p>
                        </p>

                        <hr />
                        <p>
                          <h5>Key Risks </h5>
                          <p>
                            <strong>What are the key risks involved?</strong>
                          </p>
                          <ul>
                            <li>
                              Penguin Secure investors may be exposed to these
                              key risks, but not limited to, Basis Risk, Credit
                              Risk, Default Risk, Legal Risk, Counterparty Risk,
                              Custodian Risk, Market Risk, Liquidity Risk,
                              Operational Risk, Duration Risk, Sovereign Risk,
                              Event Risk, Decoupling Risk, and Taxation Risk
                            </li>
                            <li>
                              Investors subscribing with Fiat and/or any other
                              form of DPTs acknowledges that they additionally
                              underwrite Exchange Rate Risk as the Product and
                              Underlying are exclusively denominated and settled
                              in Stable coins (USDT/C)
                            </li>
                            <li>
                              In spite of all mitigation efforts put in place by
                              PST, there will always be an off chance that the
                              entire investment is derailed by an event or a
                              series of. In such circumstances, PST cannot be
                              held liable for the performance and delivery as
                              prescribed
                            </li>
                            <li>
                              An investor must carefully consider all risk
                              factors before making an investment decision.
                              Prior to subscription, an investor should consult
                              with his own legal, regulatory, tax, financial and
                              accounting advisors to the extent that he
                              considers it necessary
                            </li>
                          </ul>
                        </p>
                        <hr />
                        <p>
                          <p>
                            <h5>Returns Illustration</h5>
                          </p>
                          <p>
                            Underlying Asset: USDT
                            <br />
                            Principal Amount: 1,000,000.00
                            <br />
                            Start Date: 1/4/2024
                            <br />
                            Maturity Date: 30/6/2024
                            <br />
                            Tenor: 90
                            <br />
                            Year Basis: 356
                            <br />
                            APR: 15%
                            <br />
                            <br />
                            Yield: 36,986.30
                            <br />
                            Principal + Yield: 1,036,986.30
                            <br />
                          </p>
                        </p>
                        <hr />
                        <p>
                          <h5>DISCLAIMERS</h5>
                          <p>
                            This document is provided to you for informational
                            purposes only and does not constitute a solicitation
                            or an offer to buy or sell any products of PST.
                            Nothing in this document is to be construed as
                            legal, accounting, tax, financial or investment
                            advice to you. This document does not take into
                            account the specific investment objectives,
                            investment experience, financial situation or
                            particular needs of any particular person. You
                            should independently evaluate the contents of this
                            document and consider the suitability of any product
                            mentioned in this document taking into account your
                            own specific investment objectives, investment
                            experience, financial situation and particular
                            needs. If in doubt about the contents of this
                            document or the suitability of any service or
                            product mentioned in this document, you should
                            obtain independent financial, legal, accounting, tax
                            or other advice from your own financial or other
                            professional advisers before making a commitment to
                            purchase any investment product.
                            <br />
                            <br />
                            This document is intended only for the person to
                            whom it is issued by PST. It may not be published,
                            circulated, reproduced or distributed either in
                            whole, or in part, without our prior written
                            permission. This document is not intended for
                            distribution, publication or use by any person in
                            any jurisdiction outside Singapore or such other
                            jurisdiction as PST may determine in its sole and
                            absolute discretion, where such distribution,
                            publication or use of this document would be
                            contrary to, forbidden or restricted by, applicable
                            law or regulation or would subject PST to any
                            licensing, registration or other requirements in
                            other jurisdictions. This document has not been
                            reviewed by the Monetary Authority of Singapore or
                            any other regulator in any jurisdiction, and PST is
                            not regulated by the Monetary Authority of Singapore
                            or any other regulator in any jurisdiction.
                            <br />
                            <br />
                            This document has been prepared from sources PST
                            believes to be reliable, but we do not guarantee its
                            accuracy or completeness and do not accept liability
                            for any loss arising from its use. PST reserves the
                            right to remedy any errors that may be present in
                            this document.
                            <br />
                            <br />
                            PST and its respective officers, employees, agents
                            and representatives do not make any express or
                            implied representations, warranties or guarantees as
                            to the accuracy, timeliness, completeness or
                            reliability of the information, data or any other
                            contents of this document. PST does not make any
                            guarantee or other promise as to any results that
                            may be obtained from using our services or
                            subscribing for our products. The price and value of
                            investments mentioned and any income that might
                            accrue could fall or rise or fluctuate. Past
                            performance is not a guarantee or indication of
                            future results.
                            <br />
                            <br />
                            To the maximum extent permitted by law, PST shall
                            not be liable for any losses that you may suffer in
                            respect of your investment and/or subscription of
                            our products.
                            <br />
                            <br />
                            If you have any questions regarding this document,
                            please contact our representative.
                            <p class="c10">
                              <span class="c25">
                                * See sections on Associated Restrictions &amp;
                                Key Risks
                              </span>
                            </p>
                            <br />
                          </p>
                        </p>
                      </div>

                      <div className="text-center">
                        <a
                          href="#"
                          className={
                            'btn btn-block ' +
                            (isTermsRead ? 'btn-primary' : 'btn-grey')
                          }
                          onClick={handleTcAccept}
                        >
                          <span>Accept</span>
                        </a>
                      </div>
                    </Modal.Body>
                  </Modal>
                </>
              )}

              {step == 2 && (
                <div className="nk-pps-field form-action">
                  <div className="nk-pps-action">
                    <a
                      href="#"
                      className="btn btn-lg btn-block btn-primary"
                      onClick={handleBack}
                    >
                      <span>Back to Our Investment Products</span>
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Page;
